"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { RangeInput } from "@/components/ui/range-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState } from "react";
function ContactStrip() {
  const items = [{
    label: "DIRECT",
    value: "help@benewealth.info"
  }, {
    label: "OFFICE",
    value: "1-224-603-2619"
  }, {
    label: "LOCATION",
    value: "Flossmoor, IL 60422"
  }];
  return <div className="mt-12 md:mt-16">
      {items.map((item, i) => <div data-index={i} key={item.label} className="relative py-6 border-t border-border last:border-b">
          <div className="flex items-center justify-between gap-6">
            <span className="font-mono text-[0.6875rem] md:text-[0.75rem] tracking-[0.2em] uppercase text-primary">
              {item.label}
            </span>
            <span className="font-default font-normal text-base md:text-lg text-foreground">
              {item.value}
            </span>
          </div>
          <span className="absolute -right-1 top-0 size-1 rounded-md bg-primary" style={{
        transform: "translateY(-50%)"
      }} aria-hidden />
        </div>)}
    </div>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [budget, setBudget] = useState(50000);
  if (isSubmitted && !submitError) {
    return <div className="border-t border-b border-border py-16 text-center bg-secondary/5">
        <p className="font-mono text-[0.75rem] tracking-[0.2em] uppercase text-primary mb-4">
          <span className="text-primary">§</span> APPLICATION FILED
        </p>
        <p className="font-default font-normal text-xl md:text-2xl text-foreground mb-8 max-w-md mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="h-14 px-8 rounded-md border border-primary bg-transparent font-mono text-[0.75rem] tracking-[0.15em] uppercase text-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300">
          NEW INQUIRY
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-0">
      <FormField control={control} name="name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="border-t border-border py-6 gap-3">
            <FormLabel className="font-mono text-[0.6875rem] tracking-[0.2em] uppercase text-muted-foreground">
              Full Legal Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Alexander Sterling" {...field} className="h-14 px-0 border-0 border-b border-border rounded-md bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base md:text-lg font-default font-normal placeholder:text-muted-foreground/40 transition-colors duration-200" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Professional email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem className="border-t border-border py-6 gap-3">
            <FormLabel className="font-mono text-[0.6875rem] tracking-[0.2em] uppercase text-muted-foreground">
              Professional Email
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="a.sterling@firm.com" {...field} className="h-14 px-0 border-0 border-b border-border rounded-md bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base md:text-lg font-default font-normal placeholder:text-muted-foreground/40 transition-colors duration-200" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="border-t border-border py-6 gap-3">
            <FormLabel className="font-mono text-[0.6875rem] tracking-[0.2em] uppercase text-muted-foreground">
              Direct Phone
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(212) 555-0199" {...field} className="h-14 px-0 border-0 border-b border-border rounded-md bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base md:text-lg font-default font-normal placeholder:text-muted-foreground/40 transition-colors duration-200" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem className="border-t border-border py-6 gap-3">
            <FormLabel className="font-mono text-[0.6875rem] tracking-[0.2em] uppercase text-muted-foreground">
              Service Interest
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-14 w-full px-0 border-0 border-b border-border rounded-md bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base md:text-lg font-default font-normal text-foreground data-[placeholder]:text-muted-foreground/40">
                  <SelectValue placeholder="Select Strategic Path" />
                </SelectTrigger>
                <SelectContent className="rounded-md border border-border bg-popover">
                  <SelectItem value="Credit Architecture">Credit Architecture</SelectItem>
                  <SelectItem value="Business Funding">Business Funding</SelectItem>
                  <SelectItem value="Wealth Strategy">Wealth Strategy</SelectItem>
                  <SelectItem value="Mentoring">Mentoring & Coaching</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_type" render={({
      field
    }) => <FormItem className="border-t border-border py-6 gap-4">
            <FormLabel className="font-mono text-[0.6875rem] tracking-[0.2em] uppercase text-muted-foreground">
              Entity Type
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="flex flex-wrap gap-3">
                {["Personal", "Business", "Both"].map((option, i) => <label data-index={i} key={option} className="flex items-center gap-2 px-5 py-2.5 rounded-md border border-border cursor-pointer hover:border-primary transition-all duration-200 has-[:checked]:bg-primary has-[:checked]:text-primary-foreground has-[:checked]:border-primary">
                    <RadioGroupItem value={option} className="sr-only" />
                    <span className="font-mono text-[0.75rem] tracking-[0.15em] uppercase">
                      {option}
                    </span>
                  </label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Financial goals are required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="border-t border-border py-6 gap-3">
            <FormLabel className="font-mono text-[0.6875rem] tracking-[0.2em] uppercase text-muted-foreground">
              Financial Goals & Objectives
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your current capital requirements and long-term wealth objectives..." rows={6} {...field} className="min-h-[140px] px-0 border-0 border-b border-border rounded-md bg-transparent focus-visible:ring-0 focus-visible:border-primary text-base md:text-lg font-default font-normal placeholder:text-muted-foreground/40 transition-colors duration-200 resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget" render={({
      field
    }) => <FormItem className="border-t border-b border-border py-6 gap-4">
            <div className="flex items-center justify-between">
              <FormLabel className="font-mono text-[0.6875rem] tracking-[0.2em] uppercase text-muted-foreground">
                Desired Funding Amount
              </FormLabel>
              <span className="font-mono text-[0.75rem] tracking-[0.15em] text-primary font-bold">
                ${budget.toLocaleString()}
              </span>
            </div>
            <FormControl>
              <RangeInput min={10000} max={1000000} step={10000} value={budget} onValueChange={v => {
          setBudget(v);
          field.onChange(v);
        }} className="w-full h-[2px] bg-border rounded-md appearance-none cursor-pointer accent-primary" />
            </FormControl>
            <div className="flex justify-between font-mono text-[0.6875rem] tracking-[0.15em] text-muted-foreground">
              <span>$10k</span>
              <span>$1M+</span>
            </div>
          </FormItem>} />

      {submitError && <div className="mt-6 p-4 border border-destructive/30 bg-destructive/5 rounded-md">
          <p className="font-mono text-[0.75rem] tracking-[0.15em] uppercase text-destructive">
            {submitError}
          </p>
        </div>}

      <div className="mt-10">
        <div className="text-xs text-foreground text-center mb-4 leading-relaxed">
          By submitting this form you consent to receive SMS and phone communications from BeneWealth Financial regarding the programs you selected. Message and data rates may apply. Reply STOP to opt out at any time.
        </div>
        <Button type="submit" disabled={isSubmitting} className="group h-14 px-8 rounded-md bg-primary border border-primary text-primary-foreground font-mono text-[0.75rem] tracking-[0.2em] uppercase hover:bg-transparent hover:text-foreground transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              PROCESSING...
            </> : <>
              SUBMIT APPLICATION
              <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function ReachOurOffice() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1241" id="reach-our-office" className="relative overflow-hidden bg-background py-28 md:py-40">
      <div aria-hidden className="pointer-events-none absolute -bottom-16 -right-8 md:-bottom-24 md:-right-16 select-none" style={{
      opacity: 0.03
    }}>
        <span className="font-default text-[14rem] md:text-[22rem] leading-none tracking-tighter text-transparent" style={{
        WebkitTextStroke: "1px var(--primary)",
        fontWeight: 100
      }}>
          06
        </span>
      </div>

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-20">
        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className={`lg:col-span-5 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
            <div className="flex items-center gap-3 mb-8">
              <span className="font-mono text-[0.75rem] md:text-[0.8125rem] tracking-[0.2em] uppercase">
                <span className="text-primary">§</span>
                <span className="text-muted-foreground"> 06 — CONSULTATION</span>
              </span>
            </div>

            <div className="relative mb-10">
              <div className="h-px bg-border w-full" />
              <span className="absolute right-0 top-1/2 size-1 rounded-md bg-primary" style={{
              transform: "translate(50%, -50%)"
            }} aria-hidden />
            </div>

            <h2 className="font-default font-medium text-foreground leading-[1.1] tracking-tight text-[clamp(2.5rem,4.5vw,4.5rem)]">
              Architect Your <span className="font-serif italic font-light text-primary">Wealth</span>.
            </h2>

            <p className="mt-8 font-default font-normal text-muted-foreground leading-[1.6] text-[clamp(1.125rem,1.5vw,1.25rem)] max-w-md">
              We operate with extreme selectivity. Complete the qualification review to begin your journey toward elite credit architecture and strategic funding.
            </p>

            <ContactStrip />
            
            <p className="mt-12 font-mono text-[0.625rem] tracking-[0.1em] text-muted-foreground/60 uppercase max-w-xs">
              Disclaimer: No guaranteed credit score increases, funding approvals, or specific financial outcomes.
            </p>
          </div>

          <div className={`lg:col-span-7 transition-all duration-700 ease-out delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
            <FormProvider formKey="reach_office_form_contact" formId="reach_office_form_contact" sectionName="reach-our-office" defaultValues={{
            name: "",
            email: "",
            phone: "",
            inquiry_type: "",
            project_type: "",
            message: "",
            budget: 50000
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Submit Application" successMessage="Your qualification review has been initiated. A senior strategist will contact you within 48 hours." errorMessage="The secure server encountered an error. Please re-submit your application.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}